import { getGenericFormInitialValues } from "../../../components/form/utils/initialValues";
import { PhoneNumberType } from "../../../constants";

// Must match the optionItems in TevaCopayFormJson.json for alternateContactRelationshipToPatient.
// Values not in this set (e.g. "HCP" from patient demographics) are treated as invalid and cleared.
const VALID_ALTERNATE_CONTACT_RELATIONSHIPS = new Set([
  "spouse",
  "father",
  "mother",
  "dependant",
  "self",
  "other",
  "child" // legacy — kept so existing saved values aren't cleared on re-open
]);

export const getTevaCopayFormInitialValues = ({ applicationForm, formJson }) => {
  const initialValues = getGenericFormInitialValues(applicationForm, formJson);
  const patientData = applicationForm?.formData?.patient;

  if (initialValues?.patient?.primaryLanguage) {
    const language = initialValues.patient.primaryLanguage.toLowerCase();
    if (language !== "english") {
      initialValues.patient.primaryLanguage = "other";
    }
  }

  if (initialValues?.patient) {
    initialValues.patient.phoneType = initialValues.patient.phoneType || PhoneNumberType.MOBILE;
    // getGenericFormInitialValues already reads alternate contact fields from the nested
    // formData.patient.alternateContact path (via subSectionName). We only fall back to the
    // flat patient-demographics path for the initial load before any form save.
    const existingAc = initialValues.patient.alternateContact || {};
    initialValues.patient.alternateContact = {
      alternateContactFirstName: existingAc.alternateContactFirstName ?? patientData?.alternateContactFirstName,
      alternateContactMiddleName: existingAc.alternateContactMiddleName ?? patientData?.alternateContactMiddleName,
      alternateContactLastName: existingAc.alternateContactLastName ?? patientData?.alternateContactLastName,
      alternateContactPhone: existingAc.alternateContactPhone ?? patientData?.alternateContactPhone,
      alternateContactPhoneType:
        existingAc.alternateContactPhoneType ?? patientData?.alternateContactPhoneType ?? PhoneNumberType.MOBILE,
      alternateContactEmail: existingAc.alternateContactEmail ?? patientData?.alternateContactEmail,
      alternateContactRelationshipToPatient: (() => {
        const raw =
          existingAc.alternateContactRelationshipToPatient ?? patientData?.alternateContactRelationshipToPatient;
        const normalized = typeof raw === "string" ? raw.toLowerCase() : null;
        return VALID_ALTERNATE_CONTACT_RELATIONSHIPS.has(normalized) ? normalized : null;
      })()
    };

    // Derive hasAlternateContact from saved data when it's not already set.
    // The generic initialValues transform may not persist this checkbox when alternate
    // contact comes from patient record fields rather than the form submission payload.
    if (!initialValues.patient.hasAlternateContact) {
      const ac = initialValues.patient.alternateContact;
      initialValues.patient.hasAlternateContact = !!(
        ac.alternateContactFirstName ||
        ac.alternateContactLastName ||
        ac.alternateContactPhone ||
        ac.alternateContactEmail ||
        ac.alternateContactRelationshipToPatient
      );
    }

    // Normalize to a proper boolean. The raw value from the patient data store can be
    // a non-boolean truthy value (e.g. the string "true" or integer 1 from MySQL).
    // The visibility condition uses strict equality (=== true), so a non-boolean
    // truthy value would fail the check and leave the alternate-contact fields hidden.
    initialValues.patient.hasAlternateContact = !!initialValues.patient.hasAlternateContact;
  }

  return initialValues;
};
